import {
  ShieldCheck,
  CheckCircle,
  DollarSign,
  Home,
  ThumbsUp,
  Ruler,
  CalendarClock,
  Hammer,
  Users,
  Wrench,
} from 'lucide-react';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/flat-roof.js';



export default function FlatRoofPage() {
  const service = {
    title: 'Flat Roof Solutions',
    image: '/hero/best-flat-roof-services-in-los-angeles.webp',
    description:
      'Protect your home with energy-efficient flat roofing by Modern Build. High-performance, long-lasting results. Free estimate: (323) 300-4130',
    link: '/services/flat-roof',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

  
      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Flat Roof Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Flat Roof Installation in Los Angeles | Free Estimate | Modern Build</title>
        <meta name="title" content="Flat Roof Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="description"
          content="Protect your home with energy-efficient flat roofing by Modern Build. High-performance, long-lasting results. Free estimate: (323) 300-4130"
        />
        <meta
          name="keywords"
          content="flat roof Los Angeles, flat roofing contractor, flat roof installation, waterproof flat roof, TPO roofing, Modern Build"
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/flat-roof" />

        <meta property="og:title" content="Flat Roof Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          property="og:description"
          content="Protect your home with energy-efficient flat roofing by Modern Build. High-performance, long-lasting results. Free estimate: (323) 300-4130"
        />
        <meta
          property="og:image"
          content="https://modernbuildremodel.com/hero/best-flat-roof-services-in-los-angeles.webp"
        />
        <meta property="og:url" content="https://modernbuildremodel.com/services/flat-roof" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Flat Roof Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="twitter:description"
          content="Protect your home with energy-efficient flat roofing by Modern Build. High-performance, long-lasting results. Free estimate: (323) 300-4130"
        />
        <meta
          name="twitter:image"
          content="https://modernbuildremodel.com/hero/best-flat-roof-services-in-los-angeles.webp"
        />
        <meta name="twitter:site" content="@modernbuildhome" />


        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Flat Roof Installation in Los Angeles | Free Estimate | Modern Build',
                description:
                  'Modern Build offers flat roof installation services using high-performance materials designed for California’s weather. Trusted by homeowners and commercial clients across Los Angeles.',
                slug: '/services/flat-roof',
              })
            ),
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="flatRoof" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="BUILT TO PERFORM"
        title="Expert Flat Roof Installation in Los Angeles"
        background="white"
        description="Flat roofs offer sleek aesthetics, easy access, and high energy efficiency. Whether you're upgrading your home or need a new flat roof for a commercial property, we provide top-tier materials and craftsmanship."
        extraText="Our team ensures waterproofing, drainage, and insulation are done right for maximum longevity and performance."
        phone="(323) 300-4130"
        image1="/split/flat-roof-installation-los-angeles.webp"
        image2="/split/commercial-flat-roof-los-angeles.webp"
      />

      <Benefits
        title="Why Choose Modern Build for Flat Roofing?"
        subtitle="Durable materials, flawless execution"
        items={[
          {
            icon: <ShieldCheck className="w-8 h-8 text-primary" />,
            title: "Work Performed by Licensed, Bonded & Insured Installers",
            href: "/about-us",
          },
          {
            icon: <CheckCircle className="w-8 h-8 text-primary" />,
            title: "TPO, Torch Down, & Cool Roof Systems",
            href: "/services/tpo-roof",
          },
          {
            icon: <Wrench className="w-8 h-8 text-primary" />,
            title: "New Installs & Full Replacements",
          },
          {
            icon: <Ruler className="w-8 h-8 text-primary" />,
            title: "Precision Sloping & Drainage",
          },
          {
            icon: <Hammer className="w-8 h-8 text-primary" />,
            title: "Fully Waterproofed Sealing",
          },
          {
            icon: <DollarSign className="w-8 h-8 text-primary" />,
            title: "Energy Savings & Rebates",
            href: "/services/energy-efficient-windows",
          },
          {
            icon: <ThumbsUp className="w-8 h-8 text-primary" />,
            title: "Modern, Minimalist Look",
          },
          {
            icon: <Home className="w-8 h-8 text-primary" />,
            title: "Residential & Commercial Projects",
            href: "/projects",
          },
        ]}
      />

      <ContentBlock
        subtitle="ENGINEERED FOR CALIFORNIA CLIMATE"
        title="Flat Roofing That Lasts"
        description="Our flat roofs are designed to withstand Los Angeles sun, rain, and seasonal shifts. We work with advanced membranes, thermal insulation, and seamless sealants to ensure long-term protection."
        extraText="From Beverly Hills homes to commercial buildings downtown, our flat roofs deliver performance and style."
        buttonText="Request Your Flat Roof Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Flat Roofing Process"
        subtitle="From design to flawless finish"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Free On-Site Assessment",
            description: "We evaluate your roof structure, drainage, and access to propose the best solution.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "System Selection",
            description: "Choose from TPO, torch down, or hybrid materials based on your needs and budget.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Professional Installation",
            description: "We install the roofing system with thermal insulation, flashing, and sealed membranes.",
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Final Inspection",
            description: "Our team verifies water resistance, finish quality, and site cleanliness before sign-off.",
          },
        ]}
      />

      <Split
        subtitle="SUSTAINABLE & STYLISH"
        title="Flat Roofs That Add Value"
        background="white"
        description="A flat roof isn’t just a structural choice, it's a design statement. Whether used for rooftop patios, solar panels, or pure aesthetics, it adds functional and visual value to your property."
        extraText="We also offer maintenance plans and 10+ year warranties on all flat roof systems."
        phone="(323) 300-4130"
        image1="/split/flat-roof-replacement-los-angeles.webp"
        image2="/split/flat-roofing-services-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Get a Free Flat Roof Consultation"
        subtitle="We install long-lasting flat roofs across Los Angeles"
        buttonText="Book Now"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="flat-roof" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
